import { useState } from 'react';
import { ChevronDown, ChevronRight, CheckCircle2, Circle, ArrowRight, School, Building2 } from 'lucide-react';

type CenterMode = 'single' | 'cra' | null;

interface StepProps {
  number: number;
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

function Step({ number, title, children, defaultOpen = false }: StepProps) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-3 w-full px-4 py-3 text-left bg-white hover:bg-gray-50 transition-colors"
      >
        <span className="flex items-center justify-center w-7 h-7 rounded-full bg-indigo-600 text-white text-sm font-bold shrink-0">
          {number}
        </span>
        <span className="flex-1 font-medium text-gray-900">{title}</span>
        {open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
      </button>
      {open && (
        <div className="px-4 pb-4 pt-2 border-t border-gray-100 bg-gray-50/50">
          {children}
        </div>
      )}
    </div>
  );
}

function Tip({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
      <strong>💡 Consejo:</strong> {children}
    </div>
  );
}

function NavPath({ path }: { path: string[] }) {
  return (
    <span className="inline-flex items-center gap-1 text-sm font-medium text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded">
      {path.map((item, i) => (
        <span key={i} className="flex items-center gap-1">
          {i > 0 && <ArrowRight className="w-3 h-3 text-indigo-400" />}
          {item}
        </span>
      ))}
    </span>
  );
}

function CheckItem({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2">
      <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
      <span className="text-sm text-gray-700">{children}</span>
    </li>
  );
}

function TodoItem({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2">
      <Circle className="w-4 h-4 text-gray-300 mt-0.5 shrink-0" />
      <span className="text-sm text-gray-700">{children}</span>
    </li>
  );
}

export function Help() {
  const [centerMode, setCenterMode] = useState<CenterMode>(null);

  return (
    <div className="max-w-3xl mx-auto py-8 px-4 sm:px-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Guía de Configuración</h1>
        <p className="text-gray-600">
          Sigue estos pasos para configurar tu centro educativo desde cero. 
          Primero, selecciona qué tipo de centro tienes.
        </p>
      </div>

      {/* Mode selector */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-800 mb-3">¿Qué tipo de centro tienes?</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button
            onClick={() => setCenterMode('single')}
            className={`relative flex flex-col items-center gap-3 p-6 rounded-xl border-2 transition-all ${
              centerMode === 'single'
                ? 'border-indigo-500 bg-indigo-50 shadow-md'
                : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
            }`}
          >
            <Building2 className={`w-10 h-10 ${centerMode === 'single' ? 'text-indigo-600' : 'text-gray-400'}`} />
            <div className="text-center">
              <p className="font-semibold text-gray-900">Centro Único</p>
              <p className="text-sm text-gray-500 mt-1">
                Un conservatorio o escuela de música con una sola sede
              </p>
            </div>
            {centerMode === 'single' && (
              <CheckCircle2 className="absolute top-3 right-3 w-5 h-5 text-indigo-600" />
            )}
          </button>

          <button
            onClick={() => setCenterMode('cra')}
            className={`relative flex flex-col items-center gap-3 p-6 rounded-xl border-2 transition-all ${
              centerMode === 'cra'
                ? 'border-indigo-500 bg-indigo-50 shadow-md'
                : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
            }`}
          >
            <School className={`w-10 h-10 ${centerMode === 'cra' ? 'text-indigo-600' : 'text-gray-400'}`} />
            <div className="text-center">
              <p className="font-semibold text-gray-900">CRA (Centro Rural Agrupado)</p>
              <p className="text-sm text-gray-500 mt-1">
                Un centro con varias sedes/pueblos que comparten gestión
              </p>
            </div>
            {centerMode === 'cra' && (
              <CheckCircle2 className="absolute top-3 right-3 w-5 h-5 text-indigo-600" />
            )}
          </button>
        </div>
      </div>

      {/* Steps */}
      {centerMode && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold text-gray-800 mb-1">
            Pasos de configuración {centerMode === 'cra' ? '(CRA)' : '(Centro Único)'}
          </h2>

          {/* Step 1: Center config */}
          <Step number={1} title="Configurar los datos del centro" defaultOpen>
            <p className="text-sm text-gray-700 mb-3">
              Lo primero es establecer la identidad de tu centro. Ve a:
            </p>
            <p className="mb-3"><NavPath path={['Configuración', 'Configuración del Centro']} /></p>
            <ul className="space-y-2">
              <CheckItem>Escribe el <strong>nombre del centro</strong> (aparecerá en los boletines)</CheckItem>
              <CheckItem>Sube el <strong>logo</strong> del centro (opcional, aparece en el encabezado del boletín)</CheckItem>
              {centerMode === 'cra' ? (
                <CheckItem>Activa la opción <strong>"Este centro es un CRA"</strong> para habilitar la gestión de pueblos</CheckItem>
              ) : (
                <CheckItem>Deja desactivada la opción "Este centro es un CRA"</CheckItem>
              )}
            </ul>
            <Tip>El nombre y logo que configures aquí se usarán automáticamente en todos los boletines de notas.</Tip>
          </Step>

          {/* Step 1.5: Pueblos (CRA only) */}
          {centerMode === 'cra' && (
            <Step number={2} title="Añadir los pueblos del CRA">
              <p className="text-sm text-gray-700 mb-3">
                Al ser un CRA, necesitas registrar cada sede/pueblo. Ve a:
              </p>
              <p className="mb-3"><NavPath path={['Configuración', 'Pueblos del CRA']} /></p>
              <ul className="space-y-2">
                <CheckItem>Haz clic en <strong>"Añadir pueblo"</strong> para cada sede</CheckItem>
                <CheckItem>Escribe el nombre del pueblo (ej: "Pueblo A", "Pueblo B")</CheckItem>
                <CheckItem>Cuando des de alta alumnos, podrás asignarles un pueblo</CheckItem>
              </ul>
              <Tip>Los pueblos te permiten organizar y filtrar alumnos por sede. Si un alumno cambia de pueblo, puedes editarlo en cualquier momento.</Tip>
            </Step>
          )}

          {/* Step 2/3: Academic Year */}
          <Step number={centerMode === 'cra' ? 3 : 2} title="Crear el año académico">
            <p className="text-sm text-gray-700 mb-3">
              Cada curso escolar tiene su propio año académico. Ve a:
            </p>
            <p className="mb-3"><NavPath path={['Configuración', 'Año Académico']} /></p>
            <ul className="space-y-2">
              <CheckItem>Crea un año académico (ej: <strong>"2025-2026"</strong>)</CheckItem>
              <CheckItem>Márcalo como <strong>activo</strong> — solo puede haber uno activo a la vez</CheckItem>
            </ul>
            <Tip>El año académico activo determina qué datos se muestran en toda la aplicación. Al empezar un nuevo curso, crea uno nuevo y actívalo.</Tip>
          </Step>

          {/* Step 3/4: Levels */}
          <Step number={centerMode === 'cra' ? 4 : 3} title="Crear los niveles educativos">
            <p className="text-sm text-gray-700 mb-3">
              Los niveles agrupan los cursos según su etapa educativa. Ve a:
            </p>
            <p className="mb-3"><NavPath path={['Configuración', 'Niveles']} /></p>
            <ul className="space-y-2">
              <CheckItem>Crea los niveles que tenga tu centro, por ejemplo:</CheckItem>
            </ul>
            <div className="mt-2 ml-6 space-y-1">
              <p className="text-sm text-gray-600">• <strong>E. Elementales</strong> (Enseñanzas Elementales)</p>
              <p className="text-sm text-gray-600">• <strong>E. Profesionales</strong> (Enseñanzas Profesionales)</p>
            </div>
            <Tip>Los niveles son la forma de organizar los cursos. Un mismo "1º" puede existir en diferentes niveles (1º Elementales y 1º Profesionales son cursos distintos).</Tip>
          </Step>

          {/* Step 4/5: Instruments */}
          <Step number={centerMode === 'cra' ? 5 : 4} title="Añadir los instrumentos">
            <p className="text-sm text-gray-700 mb-3">
              Registra los instrumentos que se imparten en tu centro. Ve a:
            </p>
            <p className="mb-3"><NavPath path={['Configuración', 'Instrumentos']} /></p>
            <ul className="space-y-2">
              <CheckItem>Añade cada instrumento (ej: Piano, Guitarra, Violín, Flauta...)</CheckItem>
              <CheckItem>Estos instrumentos se asignarán luego a los alumnos</CheckItem>
            </ul>
          </Step>

          {/* Step 5/6: Courses */}
          <Step number={centerMode === 'cra' ? 6 : 5} title="Crear los cursos">
            <p className="text-sm text-gray-700 mb-3">
              Ahora que tienes niveles, puedes crear los cursos. Ve a:
            </p>
            <p className="mb-3"><NavPath path={['Configuración', 'Cursos']} /></p>
            <ul className="space-y-2">
              <CheckItem>Crea cada curso asignándole un <strong>nombre</strong> (ej: "1º", "2º") y un <strong>nivel</strong></CheckItem>
              <CheckItem>El curso quedará asociado al año académico activo automáticamente</CheckItem>
            </ul>
            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
              <strong>Ejemplo:</strong> Si tienes Elementales y Profesionales, crearías:
              <div className="mt-1 grid grid-cols-2 gap-1 text-xs">
                <span>• 1º → E. Elementales</span>
                <span>• 1º → E. Profesionales</span>
                <span>• 2º → E. Elementales</span>
                <span>• 2º → E. Profesionales</span>
                <span>• 3º → E. Elementales</span>
                <span>• 3º → E. Profesionales</span>
                <span>• 4º → E. Elementales</span>
                <span>• etc.</span>
              </div>
            </div>
          </Step>

          {/* Step 6/7: Group Subjects */}
          <Step number={centerMode === 'cra' ? 7 : 6} title="Configurar asignaturas grupales">
            <p className="text-sm text-gray-700 mb-3">
              Las asignaturas grupales son las materias que se imparten a todo el grupo (no son individuales por instrumento). Ve a:
            </p>
            <p className="mb-3"><NavPath path={['Configuración', 'Asignaturas Grupales']} /></p>
            <ul className="space-y-2">
              <CheckItem>Añade las asignaturas grupales (ej: Lenguaje Musical, Coro, Orquesta, Música de Cámara...)</CheckItem>
            </ul>
            <Tip>Las asignaturas grupales se diferencian de la asignatura de instrumento, que es individual para cada alumno.</Tip>
          </Step>

          {/* Step 7/8: Course Templates */}
          <Step number={centerMode === 'cra' ? 8 : 7} title="Crear plantillas de curso (Boletines)">
            <p className="text-sm text-gray-700 mb-3">
              Las plantillas definen la estructura del boletín para cada curso. Ve a:
            </p>
            <p className="mb-3"><NavPath path={['Configuración', 'Plantillas de Curso']} /></p>
            <ul className="space-y-2">
              <CheckItem>Crea una plantilla para cada combinación de nivel/curso que necesite un boletín diferente</CheckItem>
              <CheckItem>Define qué secciones y campos aparecerán en el boletín</CheckItem>
              <CheckItem>Asigna la plantilla a los cursos correspondientes</CheckItem>
            </ul>
            <p className="text-sm text-gray-700 mt-3">
              Luego en <NavPath path={['Boletines']} /> podrás diseñar visualmente el boletín: 
              añadir secciones, criterios de evaluación, observaciones, etc.
            </p>
          </Step>

          {/* Step 8/9: Evaluation Criteria */}
          <Step number={centerMode === 'cra' ? 9 : 8} title="Definir criterios de evaluación">
            <p className="text-sm text-gray-700 mb-3">
              Los criterios son las calificaciones posibles (ej: notas numéricas, letras...). Ve a:
            </p>
            <p className="mb-3"><NavPath path={['Configuración', 'Criterios de Evaluación']} /></p>
            <ul className="space-y-2">
              <CheckItem>Configura los valores posibles para las calificaciones</CheckItem>
              <CheckItem>Ejemplo: Sobresaliente, Notable, Bien, Suficiente, Insuficiente</CheckItem>
              <CheckItem>O numéricos: 1, 2, 3, 4, 5, 6, 7, 8, 9, 10</CheckItem>
            </ul>
          </Step>

          {/* Step 9/10: Teachers */}
          <Step number={centerMode === 'cra' ? 10 : 9} title="Dar de alta profesores">
            <p className="text-sm text-gray-700 mb-3">
              Registra al equipo docente del centro. Ve a:
            </p>
            <p className="mb-3"><NavPath path={['Profesores']} /></p>
            <ul className="space-y-2">
              <CheckItem>Añade cada profesor con su nombre, apellidos y email</CheckItem>
              <CheckItem>Una vez creados, podrás asignarles asignaturas y cursos</CheckItem>
            </ul>
          </Step>

          {/* Step 10/11: Teacher Subjects */}
          <Step number={centerMode === 'cra' ? 11 : 10} title="Asignar asignaturas a profesores">
            <p className="text-sm text-gray-700 mb-3">
              Conecta cada profesor con lo que imparte. Ve a:
            </p>
            <p className="mb-3"><NavPath path={['Asignaturas']} /></p>
            <ul className="space-y-2">
              <CheckItem>Selecciona un profesor y asígnale los cursos y asignaturas que imparte</CheckItem>
              <CheckItem>Esto determinará qué puede ver y editar cada profesor cuando entre con su cuenta</CheckItem>
            </ul>
          </Step>

          {/* Step 11/12: Students */}
          <Step number={centerMode === 'cra' ? 12 : 11} title="Dar de alta alumnos">
            <p className="text-sm text-gray-700 mb-3">
              Registra a los alumnos del centro. Ve a:
            </p>
            <p className="mb-3"><NavPath path={['Alumnos']} /></p>
            <ul className="space-y-2">
              <CheckItem>Puedes añadirlos <strong>uno a uno</strong> o <strong>importarlos de forma masiva</strong> desde un archivo CSV/Excel</CheckItem>
              <CheckItem>Asigna a cada alumno: nombre, apellidos, curso, instrumento, email del alumno y/o de los padres</CheckItem>
              {centerMode === 'cra' && (
                <CheckItem>Asigna el <strong>pueblo/sede</strong> correspondiente a cada alumno</CheckItem>
              )}
            </ul>
            <Tip>
              El email del alumno o de los padres es imprescindible si quieres enviar los boletines por correo electrónico.
            </Tip>
          </Step>

          {/* Step 12/13: User credentials */}
          <Step number={centerMode === 'cra' ? 13 : 12} title="Crear cuentas de usuario (profesores)">
            <p className="text-sm text-gray-700 mb-3">
              Si quieres que los profesores puedan acceder a la app para introducir notas. Ve a:
            </p>
            <p className="mb-3"><NavPath path={['Configuración', 'Credenciales de Usuarios']} /></p>
            <ul className="space-y-2">
              <CheckItem>Crea un usuario y contraseña para cada profesor que necesite acceso</CheckItem>
              <CheckItem>Asigna el rol <strong>"profesor"</strong> y los permisos necesarios</CheckItem>
              <CheckItem>Con su cuenta podrán entrar y rellenar las notas de sus asignaturas</CheckItem>
            </ul>
          </Step>

          {/* Step 13/14: Email */}
          <Step number={centerMode === 'cra' ? 14 : 13} title="Configurar el correo electrónico">
            <p className="text-sm text-gray-700 mb-3">
              Para enviar boletines por email necesitas configurar las plantillas de correo. Ve a:
            </p>
            <p className="mb-3"><NavPath path={['Configuración', 'Configuración de Correo']} /></p>
            <ul className="space-y-2">
              <CheckItem>Crea una <strong>plantilla de correo</strong> con el asunto y cuerpo del email</CheckItem>
              <CheckItem>Puedes usar variables como <strong>[NOMBRE_ALUMNO]</strong>, <strong>[CURSO]</strong>, <strong>[CENTRO]</strong></CheckItem>
              <CheckItem>Estas variables se reemplazan automáticamente por los datos reales de cada alumno al enviar</CheckItem>
            </ul>
          </Step>

          {/* Ready! */}
          <div className="mt-6 p-5 bg-green-50 border border-green-200 rounded-xl">
            <h3 className="font-semibold text-green-800 mb-2">🎉 ¡Configuración completa!</h3>
            <p className="text-sm text-green-700 mb-3">
              Con todo configurado, el flujo de trabajo diario es muy sencillo:
            </p>
            <ol className="space-y-2 ml-4 list-decimal text-sm text-green-700">
              <li>Los profesores entran en <NavPath path={['Introducción de Notas']} /> y rellenan las calificaciones de sus alumnos</li>
              <li>El administrador revisa y desde la misma pantalla puede <strong>descargar el PDF</strong> del boletín individual</li>
              <li>O usar <strong>Enviar por Email</strong> para enviar un boletín individual con el PDF adjunto</li>
              <li>O usar <strong>Envío Masivo</strong> para enviar los boletines de uno o varios cursos a la vez (cada familia recibe el PDF de su hijo/a)</li>
            </ol>
          </div>

          {/* Checklist summary */}
          <div className="mt-4 p-5 bg-white border border-gray-200 rounded-xl">
            <h3 className="font-semibold text-gray-800 mb-3">📋 Resumen de configuración</h3>
            <ul className="space-y-2">
              <TodoItem>Datos del centro (nombre, logo{centerMode === 'cra' ? ', activar CRA' : ''})</TodoItem>
              {centerMode === 'cra' && <TodoItem>Pueblos del CRA</TodoItem>}
              <TodoItem>Año académico activo</TodoItem>
              <TodoItem>Niveles educativos</TodoItem>
              <TodoItem>Instrumentos</TodoItem>
              <TodoItem>Cursos (asignados a niveles)</TodoItem>
              <TodoItem>Asignaturas grupales</TodoItem>
              <TodoItem>Plantillas de curso / Diseño de boletines</TodoItem>
              <TodoItem>Criterios de evaluación</TodoItem>
              <TodoItem>Profesores</TodoItem>
              <TodoItem>Asignaturas → Profesores</TodoItem>
              <TodoItem>Alumnos</TodoItem>
              <TodoItem>Cuentas de usuario para profesores</TodoItem>
              <TodoItem>Plantillas de correo electrónico</TodoItem>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
